/**
 * AttackAtlasPanel — A1..A9 attack patterns.
 *
 * Each attack pattern is rendered as a card with its summary, primary
 * rulebook codes, the seam it targets, and the structural mechanism
 * that neutralizes it. Filter by seam to walk the atlas along one
 * structural axis.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import attacksData from "../data/attacks.json" with { type: "json" };
import seamsData from "../data/seams.json" with { type: "json" };

const ACCENT = "#ec4899";

type SeamId = "admission" | "resource" | "authority" | "meta" | "establishment";

interface Attack {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
  readonly primaryRules: readonly string[];
  readonly neutralizedBy: string;
  readonly seam: SeamId;
}

const ATTACKS: readonly Attack[] = attacksData.attacks as readonly Attack[];

const SEAM_COLORS: Readonly<Record<SeamId, string>> = {
  admission: "#ef4444",
  resource: "#fbbf24",
  authority: "#a78bfa",
  meta: "#5eead4",
  establishment: "#38bdf8",
};

function buildSeamLabels(): Readonly<Record<SeamId, string>> {
  const fromSeams = new Map<string, string>(
    seamsData.seams.map((s: { id: string; symbol: string }) => [
      s.id,
      s.symbol,
    ]),
  );
  return {
    admission: fromSeams.get("admission") ?? "Sᴬ",
    resource: fromSeams.get("resource") ?? "Sᴿ",
    authority: fromSeams.get("authority") ?? "Sᵁ",
    meta: fromSeams.get("meta") ?? "Sᴹ",
    establishment: "G1",
  };
}

const SEAM_LABELS: Readonly<Record<SeamId, string>> = buildSeamLabels();

const FILTERS = [
  { id: "all", label: "All A1..A9", color: ACCENT },
  {
    id: "admission",
    label: `${SEAM_LABELS.admission} — admission`,
    color: SEAM_COLORS.admission,
  },
  {
    id: "resource",
    label: `${SEAM_LABELS.resource} — resource`,
    color: SEAM_COLORS.resource,
  },
  {
    id: "authority",
    label: `${SEAM_LABELS.authority} — authority`,
    color: SEAM_COLORS.authority,
  },
  { id: "meta", label: `${SEAM_LABELS.meta} — meta`, color: SEAM_COLORS.meta },
  {
    id: "establishment",
    label: "establishment",
    color: SEAM_COLORS.establishment,
  },
];

export function AttackAtlasPanel(): React.JSX.Element {
  const [filter, setFilter] = useState<string>("all");

  const filtered = ATTACKS.filter((a) => filter === "all" || a.seam === filter);

  return (
    <PanelSurface.PanelShell
      title="Attack Atlas"
      subtitle="A1..A9 — nine named attack patterns, each refused by named rules"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          The A1..A9 attacks are <strong>named composites</strong> — each one
          maps to one or more deterministic rule firings under the validator.
          None of them rely on classifier completeness; the rules are total and
          fail-closed.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Filter by structural seam</PanelSurface.SectionHeading>
        <div className="mt-3.5">
          <PanelSurface.ChipRow
            items={FILTERS}
            activeId={filter}
            onChange={setFilter}
            ariaLabel="Attack filter"
          />
        </div>
      </PanelSurface.Card>

      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
        }}
      >
        {filtered.map((attack) => {
          const seamColor = SEAM_COLORS[attack.seam];
          return (
            <PanelSurface.Card key={attack.id} style={{ borderColor: `${seamColor}55` }}>
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span
                  className="font-mono text-[18px] font-extrabold"
                  style={{
                    color: seamColor,
                  }}
                >
                  {attack.id}
                </span>
                <span className="text-[14.5px] font-bold text-[var(--ilg-bright)]">
                  {attack.label}
                </span>
                {PanelSurface.badge(SEAM_LABELS[attack.seam], seamColor)}
              </div>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
                {attack.summary}
              </p>
              <PanelSurface.SectionHeading style={{ marginTop: 14 }}>
                Primary rule firings
              </PanelSurface.SectionHeading>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {attack.primaryRules.map((r) => PanelSurface.badge(r, panelTokens.accentAmber))}
              </div>
              <PanelSurface.SectionHeading style={{ marginTop: 14 }}>
                Structural neutralization
              </PanelSurface.SectionHeading>
              <p className="mt-2 text-[13px] leading-[1.7] text-[var(--ilg-text)] italic">
                {attack.neutralizedBy}
              </p>
            </PanelSurface.Card>
          );
        })}
      </div>
    </PanelSurface.PanelShell>
  );
}
